import { Form } from "antd";
import React, { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { handleChangedRelatedField, renderDevInfo, handleFieldAlert } from "../../GeneralScripts";
import PropTypes from "prop-types";
import "./TextareaField.less";
import { getFile, sendFile } from "../../../../services/restAPI/RestApi";
import { Message } from "../../../BP2Components";
import { useFormContext } from "../../../../context/FormContext/FormContext";
import { useDebouncedCallback } from "use-debounce";
import FieldInfo from "../FieldInfo/FieldInfo";
import { getAllFieldsWithAlerts, handleTemplateAlert } from "../../../../util/Scripts";

// Because Safari does not support Regex negative lookbehind, we need to specify list of
// strings that cannot be inside a link for it to be replaced with a tag
const matchFailers = [
   ".jpg",
   ".webp",
   ".jpeg",
   ".png",
   "googleusercontent",
   "https://drive.google.com/uc?id=",
   "https://b2ng.bpower2.com/cloud.storage",
];

const TextareaField = ({
   isRequired,
   element,
   form,
   disabled,
   isVisible,
   insideGrid = false,
   initialValue,
   setChangelogData,
   changelog,
}) => {
   const {
      formData,
      changeFieldPendingStatus,
      fieldsInfo,
      setFieldsInfo,
      formId,
      fieldOptions,
      fieldEnumMap,
   } = useFormContext();
   const [value, setValue] = useState();

   const intl = useIntl();
   const ref = useRef(null);
   const isFocused = useRef(false);
   const lastRenderedHtml = useRef(null);
   const { getFieldDecorator, setFieldsValue } = form;
   const [infoText, setInfoText] = useState(null);
   const translatedLabel = element.label;
   const initValue =
      (initialValue !== "" && initialValue ? initialValue : null) ??
      (element.value !== "" && element.value ? element.value : null);

   const infoClass =
      fieldsInfo[element.tech_name] && fieldsInfo[element.tech_name] !== ""
         ? "textarea-field-info"
         : "";
   const allFieldsWithAlerts = getAllFieldsWithAlerts(formData?.forms_data?.tab?.[0]?.elements);
   const handleDebouncedChange = useDebouncedCallback(() => {
      handleChangedRelatedField(
         element,
         form,
         setFieldsInfo,
         allFieldsWithAlerts,
         formId,
         intl,
         formData._formKey,
         null,
         changeFieldPendingStatus,
         fieldEnumMap,
      );
      element.hasAlert &&
         handleFieldAlert(
            element.id,
            element.tech_name,
            form,
            formData._formKey,
            setFieldsInfo,
            fieldEnumMap,
         );
   }, 1000);

   const changeFieldValue = (value) => {
      setValue(value);
      form.setFieldsValue({ [element.tech_name]: value });
   };

   useEffect(() => {
      // Changelog
      if (isVisible(element)) {
         const initVal = initialValue ?? element.value;
         if (initVal) {
            setChangelogData && setChangelogData(element.tech_name, initVal);
         }
      }
   }, []);

   useEffect(() => {
      if (!fieldOptions[element.tech_name]) return;
      const newValue = fieldOptions[element.tech_name][0]?.label;
      changeFieldValue(newValue);
      if (isVisible(element) && setChangelogData) {
         setChangelogData(element.tech_name, newValue);
      }
   }, [JSON.stringify(fieldOptions)]);

   const isTextarea = () => {
      // Sometimes textarea in formbuilder is used as plain text.
      // To know when to use it as normal textarea we need to test those values.
      if (element.label === "" && disabled) {
         return false;
      } else {
         return true;
      }
   };

   const handlePaste = async (event) => {
      const urls = [];
      const items = event.clipboardData.items;

      for (var i = 0; i < items.length; i++) {
         if (items[i]?.type.indexOf("image") === -1) continue;

         try {
            Message.loading(intl.formatMessage({ id: "global.loading" }), 0);
            const file = items[i].getAsFile();
            const formData = new FormData();
            formData.append("multipartFile", file);

            const response = await sendFile(formData);
            const fileUrlResponse = await getFile(response.data);
            urls.push(fileUrlResponse.data);

            Message.success(intl.formatMessage({ id: "global.success" }));
         } catch (error) {
            console.log(error);
            Message.error(intl.formatMessage({ id: "global.error" }));
         } finally {
            Message.destroy();
         }
      }
      try {
         const imgTags = ref.current.querySelectorAll("img");
         const imgsToChange = Array.from(imgTags).filter((tag) =>
            tag.currentSrc.includes("data:image"),
         );

         imgsToChange.forEach((img, i) => {
            if (urls[i] === undefined) return;
            img.src = urls[i];
            img.referrerPolicy = "no-referrer";
            img.alt = "Google image";
         });
      } catch (error) {
         console.log(error);
      }
   };

   const handleLinks = (value) => {
      try {
         //this regex search for http(s) links that are not inside href attribute.
         const urlRegex =
            /(?<!(href="|>))https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)(?!")/g;

         const changed = value.replaceAll(urlRegex, (match) => {
            if (matchFailers.some((failer) => match.includes(failer))) return match;
            return `<a href=${match} target='_blank' style='cursor: pointer;' contentEditable='false'>${match}</a>`;
         });

         if (!ref.current || value === changed) return value;

         // Save previous cursor index
         const selection = window.getSelection();

         const rangeCopy = selection?.getRangeAt(0).cloneRange();

         const prevSelectionDetails = {
            endContainer: rangeCopy?.endContainer,
            endOffset: rangeCopy?.endOffset,
         };

         // Update HTML with new anchor tags
         ref.current.innerHTML = changed;

         const newRange = new Range();

         const textNode = document
            .evaluate(
               '//*[text()="' + prevSelectionDetails.endContainer?.nodeValue + '"]',
               document,
               null,
               XPathResult.ORDERED_NODE_SNAPSHOT_TYPE,
            )
            .snapshotItem(0);

         // Set cursor index
         selection.removeAllRanges();
         newRange.collapse(true);
         newRange.setStartAfter(textNode);
         selection.addRange(newRange);
         return changed;
      } catch {
         return value;
      }
   };

   const handleTextareaInput = () => {
      if (!ref.current) return;
      const isEmpty = !ref.current.textContent.trim();
      setFieldsValue({ [element.tech_name]: isEmpty ? "" : ref.current.innerHTML });
      if (!isEmpty && fieldsInfo[element.tech_name]) {
         setFieldsInfo((prev) => ({ ...prev, [element.tech_name]: "" }));
      }
   };

   const handleTextareaChange = () => {
      isFocused.current = false;
      const textareaWasOnlyClicked =
         (ref.current.innerHTML === "" && changelog[element.tech_name]?.initial === undefined) ||
         ref.current.innerHTML === changelog[element.tech_name]?.initial;
      if (isVisible(element) && !textareaWasOnlyClicked) {
         setChangelogData &&
            setChangelogData(element.tech_name, `_anchor_${element.tech_name}`, element.label);
      }
      setFieldsValue({ [element.tech_name]: handleLinks(ref.current.innerHTML) });
      handleDebouncedChange();
   };

   const handleInitialValue = (value) => {
      if (!value) return value;
      const anchorTags = /<a /g;
      const changed = value.replaceAll(anchorTags, "<a contenteditable='false' target='_blank' ");
      if (isVisible(element)) {
         if (initValue) {
            setChangelogData && setChangelogData(element.tech_name, initValue);
         }
      }
      return changed;
   };

   const handleInitialValueChange = (value) => {
      const formValue = form.getFieldsValue()[element.tech_name];
      if (isFocused.current) return formValue ?? value;
      if (formValue !== undefined && formValue !== "" && formValue !== value) return formValue;
      return value;
   };

   useEffect(() => {
      const infoText = fieldsInfo[element.tech_name];
      setInfoText(infoText);
      handleTemplateAlert(element.tech_name, infoText);
   }, [fieldsInfo[element.tech_name]]);

   if (!isFocused.current) {
      lastRenderedHtml.current = handleInitialValue(handleInitialValueChange(initValue));
   }

   return (
      <>
         <Form.Item
            key={element.tech_name}
            label={!insideGrid && translatedLabel}
            required={isRequired(element)}
            className={`${element?.type}-field`}
            style={{
               display: isVisible(element) ? "" : "none",
               gridArea: element.tech_name.replaceAll("#", "_"),
            }}
         >
            {getFieldDecorator(element.tech_name, {
               rules: [
                  {
                     required: isRequired(element),
                     message: intl.formatMessage({ id: "field.required" }),
                  },
               ],
               initialValue: handleInitialValue(initValue),
            })(
               isTextarea() ? (
                  <div
                     ref={ref}
                     contentEditable={!disabled}
                     onPaste={handlePaste}
                     onFocus={() => {
                        isFocused.current = true;
                     }}
                     onInput={handleTextareaInput}
                     onBlur={handleTextareaChange}
                     data-disabled={!!disabled}
                     className={`textarea-field__input field_${element.tech_name} _type_${element?.type}_type_ _id_${element.id}_id_ ${infoClass} has-alert-${element.hasAlert}`}
                     dangerouslySetInnerHTML={{ __html: lastRenderedHtml.current }}
                  />
               ) : (
                  <div
                     dangerouslySetInnerHTML={{
                        __html: handleInitialValue(handleInitialValueChange(initValue)),
                     }}
                     className="textarea-field__readonly"
                  />
               ),
            )}
            {renderDevInfo(element)}
            <FieldInfo text={infoText} />
         </Form.Item>
      </>
   );
};

TextareaField.propTypes = {
   element: PropTypes.object.isRequired,
   disabled: PropTypes.bool,
   isVisible: PropTypes.func,
   form: PropTypes.any,
};

export default TextareaField;
